import React, { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import dynamic from 'next/dynamic';

import clsx from 'clsx';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';
import { stringifyValues } from '@atc/string-fns';

import { sendClick } from 'reaxl-analytics';

import { setItem } from '@/utilities/sessionStorageUtils';
import { SRP_PATH_KEY } from '@/utilities/srpPathFromSessionStorageUtils';

import { shoppingAssistantCTAClick } from '@/analytics/shoppingAssistantEvents';
import { keywordSearchSubmit } from '@/analytics/srpAnalyticsHandlers';

import activeInteractionDuck from '@/ducks/activeInteractionDuck.js';
import queryDuck from '@/ducks/queryDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import SuggestedKeywordFetcher from '@/fetchers/srp/SuggestedKeywordFetcher';

import useSrpNavigation from '@/hooks/useSrpNavigation';
import { useSuggestedSearches } from '@/hooks/useSuggestedSearches';

import SearchInput from '@/components/SearchInput';

const KeywordSearch = dynamic(() => import('@/components/KeywordSearch'), { ssr: false });

const navigateToArticlePage = (contentUrl) => {
    window.open(contentUrl);
};

export function KeywordSearchTrigger({
    onTriggerClick = () => {},
}) {

    const dispatch = useDispatch();
    const { loadSuggestions = () => {} } = useSuggestedSearches();

    const {
        suggested_keyword: [enableKeyword, { keyword_placeholder: keywordPlaceholder }],
    } = useFeatures(['suggested_keyword']);

    return enableKeyword && (
        <SearchInput
            placeholder={keywordPlaceholder}
            data-cmp="cntnr-kywrd"
            onFocus={() => {
                dispatch(activeInteractionDuck.creators.setKeys({
                    showKeywordSearchModal: true,
                }));
                onTriggerClick();
                loadSuggestions();
            }}
            aria-label={keywordPlaceholder}
        />
    );
}

function KeywordSearchModalContainer({
    isStickyHeader = false,
    onSearch = () => {},
    ...rest
}) {

    const { isBrand } = useBrand();
    const isKbb = isBrand('kbb');

    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);

    const query = useSelector(queryDuck.selectors.getDuckState);

    const filters = useSelector(srpFiltersDuck.selectors.getFiltersValues);
    const searchRadius = stringifyValues(_get(filters, 'searchRadius', 0));

    const isShoppingAssistant = useSelector(activeInteractionDuck.selectors.getIsShoppingAssistant);

    // Check if we're on a VDP page by looking for active inventory
    const vdpActiveInventory = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const isOnVdp = !!vdpActiveInventory?.id;

    // Build location object used for KeywordSearch component using query state (or filters for the SRP)
    const keywordSearchLocation = {
        zip: query.zip || _get(filters, 'zip', null),
        searchRadius: query.searchRadius || searchRadius,
    };

    const {
        advanced_keyword_search: [, { with_type_ahead: enableTypeAhead }],
        suggested_keyword: [enableKeyword, { keyword_placeholder: keywordPlaceholder }],
    } = useFeatures(['advanced_keyword_search', 'suggested_keyword']);

    const navigateToSrp = useSrpNavigation();

    const handleKeywordSearch = useCallback((data) => {
        const { codes = {}, contentUrl } = data;

        if (isShoppingAssistant) {
            sendClick(shoppingAssistantCTAClick, null, {
                input: data.rawText,
                isKbb,
                ...(isOnVdp && { tagIndex: 'vdp-161' }),
            });
        } else {
            sendClick(keywordSearchSubmit, null, {
                ...data,
                cmp: 'fts_kyw',
            });
        }

        onSearch(data);

        if (contentUrl) {
            navigateToArticlePage(contentUrl);
        } else {
            // Clear scroll position from session storage when navigating from VDP
            // This prevents the SRP from scrolling to the previous listing position
            if (isOnVdp) {
                setItem(SRP_PATH_KEY, {
                    path: '/cars-for-sale', // Use a generic path
                    scrollPosition: { left: 0, top: 0 },
                }, true);
            }

            navigateToSrp({
                action: 'replace',
                scroll: isOnVdp ? !isXs : false,
                scrollToResultsCount: isOnVdp ? isXs : false,
                resetPagination: true,
                filtersValues: { ...codes },
            });
        }
    }, [navigateToSrp, onSearch, isOnVdp, isXs, isKbb, isShoppingAssistant]);

    return enableKeyword && (
        <div
            data-cmp={`cntnr-kywrd${isStickyHeader ? '-sticky' : ''}`}
            {...rest}
        >
            <KeywordSearch
                autoFocus
                className={clsx('text-left', { 'advanced-keyword-search': enableTypeAhead })}
                fetchSuggestions={SuggestedKeywordFetcher}
                location={keywordSearchLocation}
                onSearch={handleKeywordSearch}
                placeholder={keywordPlaceholder}
                variant="modal"
            />
        </div>
    );
}

export default React.memo(KeywordSearchModalContainer, _isEqual);
